import React, { useState, useEffect } from 'react';
import { TrophyIcon, DocumentTextIcon, ExclamationTriangleIcon, ArrowPathIcon, EyeIcon } from '@heroicons/react/24/outline';
import { FunnelIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import Button from '../common/Button';
import Select from '../common/Select';
import DialogBox from '../common/DialogBox';
import ConfirmationDialog from '../common/ConfirmationDialog';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { itemsPerPageOptions } from '../../shared/constants';
import { Game, GameFilters, User } from '../../shared/interfaces';
import { getGameTitle, cleanObject, formatDateOnly } from '../../utils/util';
import MatchResultsDialog from '../game/MatchResultsDialog';
import MatchReportDialog from './MatchReportDialog';
import { useAuthorization } from '../../auth/useAuthorization';
import { Role } from '../../auth/roles';
import { GameType } from '../../shared/enume';

// Helper function to extract user name from User object or string
const getUserName = (user: User | string | undefined): string => {
  if (!user) return 'N/A';
  if (typeof user === 'string') return user;
  return user.name || user.username || 'Unknown';
};

interface GameListRequest {
  id: number | null;
  gameType: string;
  teamA: string;
  teamB: string;
  modifiedBy: string;
  modifiedOn: string | Date;
  createdOnStart: string;
  createdOnEnd: string;
  page: number;
  pageSize: number;
  orderBy: string;
  orderDirection: string;
}

const DEFAULT_PAGE_SIZE = 30;

const EMPTY_SELECT_MATCH_FILTERS: GameFilters = {
  gameType: '',
  teamA: '',
  teamB: '',
  createdOnStart: '',
  createdOnEnd: '',
};

const GAME_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: GameType.oneday, label: 'One Day' },
  { value: GameType.test, label: 'Test' },
  { value: GameType.session, label: 'Session' },
];

const SelectMatch: React.FC = () => {
  const navigate = useNavigate();
  const { hasAnyRole } = useAuthorization();
  const canRecallMatch = hasAnyRole([Role.ADMIN, Role.OWNER]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof Game>('createdOn');
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');
  const [gameListRequest, setGameListRequest] = useState<GameListRequest>({
    id: null,
    gameType: '',
    teamA: '',
    teamB: '',
    modifiedBy: '',
    modifiedOn: '',
    createdOnStart: '',
    createdOnEnd: '',
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: 'createdOn',
    orderDirection: 'desc',
  });
  const [showFilter, setShowFilter] = useState(true);
  const [filters, setFilters] = useState<GameFilters>(EMPTY_SELECT_MATCH_FILTERS);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [showMatchResults, setShowMatchResults] = useState(false);
  const [showMatchReport, setShowMatchReport] = useState(false);
  const [selectedGames, setSelectedGames] = useState<number[]>([]);
  const [selectedGamesForReport, setSelectedGamesForReport] = useState<Game[]>([]);
  const [showSelectionError, setShowSelectionError] = useState(false);
  const [showRecallConfirm, setShowRecallConfirm] = useState(false);
  const [gameToRecall, setGameToRecall] = useState<Game | null>(null);

  useEffect(() => {
    setLoading(true);
    const requestData: Record<string, unknown> = {
      ...cleanObject(gameListRequest),
      page: currentPage + 1,
      isGameOver: true,
    };
    // Empty gameType is stripped by cleanObject so the API returns all types unless filtered.

    apiService.post<{ data: Game[]; totalCount: number }>(apiRoutes.games.list, requestData)
      .then(data => {
        setGames(data.data);
        setTotalPages(Math.ceil(data.totalCount / gameListRequest.pageSize));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [gameListRequest, currentPage]);

  const handleSort = (column: keyof Game) => {
    const newOrderDirection = orderBy === column && orderDirection === 'asc' ? 'desc' : 'asc';
    setOrderBy(column);
    setOrderDirection(newOrderDirection);
    setCurrentPage(0);
    setGameListRequest(prev => ({
      ...prev,
      orderBy: column as string,
      orderDirection: newOrderDirection,
      page: 1,
    }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
    setGameListRequest(prev => ({
      ...prev,
      pageSize: newRowsPerPage,
      page: 1,
    }));
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page - 1);
    setGameListRequest(prev => ({
      ...prev,
      page: page,
    }));
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 0));
    setGameListRequest(prev => ({
      ...prev,
      page: Math.max(prev.page - 1, 1),
    }));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
    setGameListRequest(prev => ({
      ...prev,
      page: Math.min(prev.page + 1, totalPages),
    }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => {
      if (name === 'createdOnEnd' && prevFilters.createdOnStart && value && value < prevFilters.createdOnStart) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'End date cannot be earlier than start date.', type: 'error' },
        }));
        return prevFilters;
      }
      if (name === 'createdOnStart' && prevFilters.createdOnEnd && value && prevFilters.createdOnEnd < value) {
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'End date cannot be earlier than start date.', type: 'error' },
        }));
        return prevFilters;
      }
      return { ...prevFilters, [name]: value };
    });
  };

  const applySelectMatchFilters = (nextFilters: GameFilters) => {
    setCurrentPage(0);
    setSelectedGames([]);
    setGameListRequest((prev) => ({
      ...prev,
      gameType: nextFilters.gameType || '',
      teamA: nextFilters.teamA || '',
      teamB: nextFilters.teamB || '',
      createdOnStart: nextFilters.createdOnStart || '',
      createdOnEnd: nextFilters.createdOnEnd || '',
      page: 1,
    }));
  };

  const handleFilterAction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (e.currentTarget.textContent === 'Clear') {
      setFilters(EMPTY_SELECT_MATCH_FILTERS);
      setTimeout(() => applySelectMatchFilters(EMPTY_SELECT_MATCH_FILTERS), 0);
      return;
    }
    if ((filters.createdOnStart && !filters.createdOnEnd) || (!filters.createdOnStart && filters.createdOnEnd)) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Please select both Created On start and end dates.', type: 'error' },
      }));
      return;
    }
    applySelectMatchFilters(filters);
  };

  const handleViewReport = () => {
    if (selectedGames.length === 0) {
      setShowSelectionError(true);
      return;
    }
    const gamesToShow = games.filter(game => selectedGames.includes(game.id));
    setSelectedGamesForReport(gamesToShow);
    setShowMatchReport(true);
  };

  const handleCheckboxChange = (gameId: number, checked: boolean) => {
    if (checked) {
      setSelectedGames([...selectedGames, gameId]);
    } else {
      setSelectedGames(selectedGames.filter(id => id !== gameId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedGames(games.map(game => game.id));
    } else {
      setSelectedGames([]);
    }
  };

  const handleMatchResult = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedGame(game);
    setShowMatchResults(true);
  };

  const handleMatchReport = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedGame(game);
    setShowMatchReport(true);
  };

  const handleMatchRecall = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Validate user has rights to recall game
    if (!canRecallMatch) {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: 'You do not have permission to recall matches. Only Admin and Owner roles can recall games.',
          type: 'error'
        }
      }));
      return;
    }
    
    // Set the game to recall and show confirmation dialog
    setGameToRecall(game);
    setShowRecallConfirm(true);
  };

  const confirmRecall = async () => {
    if (!gameToRecall) return;
    
    setShowRecallConfirm(false);
    
    try {
      setLoading(true);
      const response = await apiService.post(apiRoutes.games.matchRecall(gameToRecall.id));
      
      // Handle success response
      if (response.success) {
        const message = response.message || `Match recalled successfully. ${response.deletedTransactions || 0} transactions deleted.`;
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: {
            message: message,
            type: 'success'
          }
        }));
        
        // Navigate to gameboard for the recalled game
        if (gameToRecall?.id) {
          navigate(`/game/transactions/${gameToRecall.id}`, { state: { fromSelectMatch: true } });
        } else {
          // Fallback: refresh the game list if navigation fails
          setGameListRequest(prev => ({
            ...prev,
            page: 1
          }));
          setCurrentPage(0);
        }
      } else {
        throw new Error(response.message || 'Failed to recall match');
      }
    } catch (error: any) {
      console.error('Error recalling match:', error);
      
      // Handle error response
      const errorMessage = error?.message || error?.error || 
        (error?.response?.data?.message) ||
        'Failed to recall match. Please try again.';
      
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
          message: errorMessage,
          type: 'error'
        }
      }));
    } finally {
      setLoading(false);
      setGameToRecall(null);
    }
  };

  const handleViewSoda = (game: Game, e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to game transactions page to view soda
    navigate(`/game/transactions/${game.id}`, { state: { viewOnly: true, fromSelectMatch: true } });
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-slate-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-2 rounded-lg shadow-sm">
              <TrophyIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Select Match</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                All completed matches (newest created first). Select matches to view results or reports.
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-2 text-slate-700">
              <span className="text-sm font-medium">Rows:</span>
              <Select
                value={rowsPerPage}
                options={itemsPerPageOptions}
                onChange={handleRowsPerPageChange}
              />
            </div>
            <Button 
              onClick={handleViewReport} 
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium px-4 py-2 rounded-lg shadow-sm transition-all duration-200"
            >
              View Report
            </Button>
            <button
              type="button"
              onClick={() => setShowFilter((prev) => !prev)}
              className={`p-2 rounded-lg transition-all duration-200 ${
                showFilter
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              title="Filters"
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {showFilter && (
        <div className="w-full mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 bg-gray-50 p-3 rounded-sm border">
          <label className="text-sm font-medium text-gray-700 flex flex-row items-center space-x-2">
            <span>Game Type</span>
            <Select
              name="gameType"
              value={filters.gameType || ''}
              options={GAME_TYPE_FILTER_OPTIONS}
              onChange={handleFilterChange}
              className="w-36"
            />
          </label>
          <label className="text-sm font-medium text-gray-700 flex flex-row items-center space-x-2">
            <span>Team A</span>
            <input
              type="text"
              name="teamA"
              value={filters.teamA || ''}
              onChange={handleFilterChange}
              className="w-40 p-1 border rounded"
            />
          </label>
          <label className="text-sm font-medium text-gray-700 flex flex-row items-center space-x-2">
            <span>Team B</span>
            <input
              type="text"
              name="teamB"
              value={filters.teamB || ''}
              onChange={handleFilterChange}
              className="w-40 p-1 border rounded"
            />
          </label>
          <label className="text-sm font-medium text-gray-700 flex flex-row items-center space-x-2">
            <span>Created On</span>
            <input
              type="date"
              name="createdOnStart"
              value={filters.createdOnStart || ''}
              onChange={handleFilterChange}
              className="w-36 p-1 border rounded"
            />
            <span>to</span>
            <input
              type="date"
              name="createdOnEnd"
              value={filters.createdOnEnd || ''}
              onChange={handleFilterChange}
              className="w-36 p-1 border rounded"
            />
          </label>
          <div className="flex-1 flex justify-end gap-1">
            <button
              type="button"
              onClick={handleFilterAction}
              className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleFilterAction}
              className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-1 w-12 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={games.length > 0 && selectedGames.length === games.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="cursor-pointer"
                  />
                </th>
                <th 
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('gameType')}
                >
                  <div className="flex items-center gap-2">
                    <span>Game Type</span>
                    {orderBy === 'gameType' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('teamA')}
                >
                  <div className="flex items-center gap-2">
                    <span>Team A</span>
                    {orderBy === 'teamA' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('teamB')}
                >
                  <div className="flex items-center gap-2">
                    <span>Team B/Overs</span>
                    {orderBy === 'teamB' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-1 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Modified By</th>
                <th 
                  className="px-2 py-1 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('createdOn')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Created On</span>
                    {orderBy === 'createdOn' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th 
                  className="px-2 py-1 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => handleSort('modifiedOn')}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span>Modified On</span>
                    {orderBy === 'modifiedOn' && (
                      <span className="text-blue-500">{orderDirection === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </div>
                </th>
                <th className="px-2 py-1 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200 text-slate-900 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center p-8">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
                      <p className="text-slate-500 text-sm font-medium">Loading games...</p>
                    </div>
                  </td>
                </tr>
              ) : games.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center p-8">
                    <div className="flex flex-col items-center justify-center">
                      <div className="bg-slate-100 rounded-full p-3 mb-3">
                        <TrophyIcon className="h-6 w-6 text-slate-400" />
                      </div>
                      <p className="text-slate-600 text-sm font-medium">No games found</p>
                      <p className="text-slate-400 text-xs mt-1">No completed matches match the current request.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                games.map((game, index) => (
                  <tr 
                    key={game.id} 
                    className={`hover:bg-blue-50 transition-colors duration-150 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    <td className="px-2 py-0.5 text-center">
                      <input
                        type="checkbox"
                        checked={selectedGames.includes(game.id)}
                        onChange={(e) => handleCheckboxChange(game.id, e.target.checked)}
                        className="cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{getGameTitle(game.gameType || '').toUpperCase()}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{game.teamA}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap">
                      <div className="text-sm text-slate-700">{game.teamB ? game.teamB : game.overs ? `${game.overs} Overs` : ''}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap">
                      <div className="text-sm text-slate-700">{getUserName(game.modifiedBy)}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap text-center">
                      <div className="text-sm text-slate-600">{formatDateOnly(game.createdOn || game.createdAt) || '—'}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap text-center">
                      <div className="text-sm text-slate-600">{formatDateOnly(game.modifiedAt || game.modifiedOn) || '—'}</div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap text-right">
                      <div className="inline-flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => handleMatchResult(game, e)}
                          className="text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-all duration-200"
                          title="Result"
                        >
                          <TrophyIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleMatchReport(game, e)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-all duration-200"
                          title="Report"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                        {canRecallMatch && (
                          <button
                            onClick={(e) => handleMatchRecall(game, e)}
                            className="text-green-600 hover:text-green-800 hover:bg-green-50 rounded-md transition-all duration-200"
                            title="Match Recall"
                          >
                            <ArrowPathIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleViewSoda(game, e)}
                          className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-all duration-200"
                          title="View Soda"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-slate-600">
                Showing page <span className="font-semibold text-slate-900">{currentPage + 1}</span> of{' '}
                <span className="font-semibold text-slate-900">{totalPages}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevious}
                  disabled={currentPage === 0}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage === 0
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isVisible = 
                      pageNumber === 1 || 
                      pageNumber === totalPages || 
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 3);
                    
                    return isVisible ? (
                      <button
                        key={index}
                        onClick={() => handlePageClick(pageNumber)}
                        className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 min-w-[40px] ${
                          currentPage === index
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    ) : pageNumber === currentPage - 2 || pageNumber === currentPage + 4 ? (
                      <span key={index} className="px-2 py-2 text-slate-400">...</span>
                    ) : null;
                  })}
                </div>
                
                <button
                  onClick={goToNext}
                  disabled={currentPage >= totalPages - 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    currentPage >= totalPages - 1
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-white text-slate-700 hover:bg-blue-50 hover:text-blue-600 border border-slate-300 shadow-sm'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Match Results Dialog */}
      {selectedGame && (
        <MatchResultsDialog
          isOpen={showMatchResults}
          onClose={() => {
            setShowMatchResults(false);
            setSelectedGame(null);
          }}
          gameDetails={{
            id: selectedGame.id,
            teamA: selectedGame.teamA || '',
            teamB: selectedGame.teamB || '',
            teamC: selectedGame.teamC || '',
            overs: selectedGame.overs || null,
            gameType: selectedGame.gameType || '',
            isGameOver: true,
          }}
        />
      )}

      {/* Match Report Dialog */}
      {showMatchReport && selectedGamesForReport.length > 0 && (
        <MatchReportDialog
          isOpen={showMatchReport}
          onClose={() => {
            setShowMatchReport(false);
            setSelectedGamesForReport([]);
          }}
          games={selectedGamesForReport}
        />
      )}
      {showMatchReport && selectedGame && selectedGamesForReport.length === 0 && (
        <MatchReportDialog
          isOpen={showMatchReport}
          onClose={() => {
            setShowMatchReport(false);
            setSelectedGame(null);
          }}
          gameId={selectedGame.id}
          gameDetails={{
            id: selectedGame.id,
            teamA: selectedGame.teamA || '',
            teamB: selectedGame.teamB || '',
            teamC: selectedGame.teamC || '',
            overs: selectedGame.overs || null,
            gameType: selectedGame.gameType || '',
            isGameOver: true,
          }}
        />
      )}

      {/* Selection Error Dialog */}
      <DialogBox
        isOpen={showSelectionError}
        onClose={() => setShowSelectionError(false)}
        title="Selection Required"
        panelClassName="max-w-md"
      >
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Games Selected
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please select at least one game to view the report.
              </p>
              <div className="flex justify-end">
                <Button
                  onClick={() => setShowSelectionError(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogBox>

      {/* Match Recall Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showRecallConfirm}
        title="Recall Match"
        message={gameToRecall ? `Are you sure you want to recall match "${gameToRecall.teamA} vs ${gameToRecall.teamB}"? This will delete all transactions and revert party balances.` : ''}
        onConfirm={confirmRecall}
        onCancel={() => {
          setShowRecallConfirm(false);
          setGameToRecall(null);
        }}
      />
    </div>
  );
};

export default SelectMatch;
